import { PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Skeleton } from "antd";
import classNames from "classnames";
import { MAX_HIGHLIGHTS_COUNT } from "components/Graph/constants.ts";
import { useKeyFormatter } from "components/Graph/hooks/useKeyFormatter.ts";
import { GRAPH_TYPE, GraphId } from "components/Graph/types.ts";
import { WipHighlight } from "components/Graph/UI/Highlights/WipHighlight.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import {
    Children,
    cloneElement,
    isValidElement,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useCallback,
    useState,
} from "react";
import { FreeMode, Mousewheel, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { PermissionEnum } from "types/enums.ts";

import "swiper/css";
import "swiper/css/scrollbar";
import "swiper/css/free-mode";

import { ExposedHighlight, HighlightProps } from "./Highlight";

import type { Swiper as SwiperType } from "swiper/types";

interface BaseProps<TData extends object, CPath extends keyof TData> {
    data: TData[];
    handleAdd: (key: CPath) => void;
    graphId: GraphId;
    active?: CPath[];
    isLoading?: boolean;
}

const sortItems = (children: ReactNode[]) => {
    return children.reduce(
        (acc, child) => {
            if (isHighlightItem(child) && child.props.hidden) {
                acc.hidden.push(child.props);
            } else if (isHighlightItem(child)) {
                acc.visible.push(child);
            }

            return acc;
        },
        { hidden: [], visible: [] } as {
            hidden: HighlightProps<object>[];
            visible: ReactElement<HighlightProps<object>>[];
        }
    );
};

const Base = <TData extends object, CPath extends keyof TData>({
    data,
    children,
    handleAdd,
    graphId,
    active,
    isLoading,
}: PropsWithChildren<BaseProps<TData, CPath>>) => {
    const { t } = usePaldockTranslation("ui.report.columns");

    const [edit, setEdit] = useState<boolean>(false);

    const { hasPermission } = usePermission();
    const canAddHighlight = hasPermission(PermissionEnum.ReportAddHighlight);

    const { hidden, visible } = sortItems(Children.toArray(children));

    const toggleEdit = () => setEdit((prev) => !prev);
    const [newHighlight, setNewHighlight] = useState<CPath>();

    const formattedValue = useKeyFormatter(data, newHighlight, graphId);

    const handleSubmit = (key: CPath) => {
        handleAdd(key);
        setNewHighlight(undefined);
        setEdit(false);
    };

    const handleCancel = () => {
        setNewHighlight(undefined);
        setEdit(false);
    };

    const overLimit = GRAPH_TYPE[graphId] === "time" ? (active?.length || 0) >= MAX_HIGHLIGHTS_COUNT : false;

    const [showRightShadow, setShowRightShadow] = useState(false);

    const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
        const wrapperEl = swiper.wrapperEl;
        const containerEl = swiper.el;
        const isScrollable = wrapperEl.scrollWidth > containerEl.clientWidth;
        const isAtEnd = swiper.isEnd;
        setShowRightShadow(isScrollable && !isAtEnd);
    }, []);

    return (
        <div className="relative">
            <Swiper
                modules={[Scrollbar, Mousewheel, FreeMode]}
                spaceBetween={8}
                slidesPerView="auto"
                freeMode={true}
                mousewheel={{ forceToAxis: true }}
                scrollbar={{ hide: true }}
                className="highlights-swiper !pt-4 !-mt-4 !overflow-y-visible"
                onProgress={handleSwiperUpdate}
                onResize={handleSwiperUpdate}
                onInit={handleSwiperUpdate}
            >
                {!edit && !overLimit && (
                    <SwiperSlide className="!w-auto">
                        <Flex className="h-[74px]">
                            <Button
                                disabled={isLoading || !canAddHighlight}
                                className="h-full"
                                onClick={toggleEdit}
                                icon={<PlusOutlined />}
                            />
                        </Flex>
                    </SwiperSlide>
                )}
                {edit && !overLimit && (
                    <SwiperSlide className="!w-auto">
                        <WipHighlight<CPath>
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            displayValue={formattedValue}
                            value={newHighlight}
                            options={hidden.map(({ metricKey }) => ({
                                value: metricKey as CPath,
                                label: t(String(metricKey)),
                            }))}
                            onChange={setNewHighlight}
                        />
                    </SwiperSlide>
                )}
                {isLoading
                    ? Array.from({ length: 5 }, (_, i) => (
                          <SwiperSlide key={`skeleton-${i}`} className="!w-auto">
                              <div className="w-[200px] h-[74px]">
                                  <Skeleton.Node active style={{ width: "100%", height: "100%" }} />
                              </div>
                          </SwiperSlide>
                      ))
                    : Children.toArray(visible)
                          .filter(isHighlightItem)
                          .map((child) => (
                              <SwiperSlide key={child.props.metricKey} className="!w-auto">
                                  {cloneElement(child, { data, graphId, active: (active || []) as string[] })}
                              </SwiperSlide>
                          ))}
            </Swiper>
            <div
                className={classNames(
                    "absolute right-0 top-4 bottom-0 w-3 pointer-events-none transition-opacity duration-200 z-10",
                    "bg-gradient-to-l from-[#e2e2e2] to-transparent",
                    showRightShadow ? "opacity-100" : "opacity-0"
                )}
            />
        </div>
    );
};

const isHighlightItem = (child: unknown): child is ReactElement<HighlightProps<object>> => {
    return isValidElement(child) && child.type === ExposedHighlight;
};

export const Highlights = Object.assign(Base, { Item: ExposedHighlight });
